import {
  filters,
  API_INFINITY_VALUE,
  FILTER_TYPE,
} from '../../../../../constants';
import type { CustomCompanyFilter } from './types';
import { dots } from '../../../../../functions';

const titleFormatter = ([_, __, min, max]: (number | string)[]) => {
  return `is between ${min} and ${max === API_INFINITY_VALUE ? '∞' : max}`;
};

export const generateCompanyFilters = (): CustomCompanyFilter[] => [
  // Risk Score
  {
    defaultValue: `["${filters.risk_score}", "between", 0, 3]`,
    ruleToValue: ([_, __, ___, value]) => value,
    rule: (value) => {
      if (typeof value === 'number') {
        return `["${filters.risk_score}", "between", 0, ${
          value === 11 ? API_INFINITY_VALUE : value
        }]`;
      }
      return `["${filters.risk_score}", "between", 0, 3]`;
    },
    id: filters.risk_score,
    label: 'Risk Score',
    subLabel: '(Number of identified risks)',
    labels: { ...dots(10, { step: 1 }), 0: '0', 5: '5', 10: '10', 11: '∞' },
    max: 11,
    min: 0,
    type: FILTER_TYPE.SLIDER,
    valueResolver: (range) => {
      return range > 10 ? 11 : range;
    },
    titleFormatter,
  },
  // 7 day price
  {
    defaultValue: `["${filters.value_return_7d}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return_7d}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return_7d,
    label: '7 Day Return',
    subLabel: '(% change over the past 7 days)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // 30 day price
  {
    defaultValue: `["${filters.value_return30d}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return30d}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return30d,
    label: '30 Day Return',
    subLabel: '(% change over the past 30 days)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // 90 day price
  {
    defaultValue: `["${filters.value_return90d}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return90d}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return90d,
    label: '90 Day Return',
    subLabel: '(% change over the past 90 days)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // 1 year price
  {
    defaultValue: `["${filters.value_return1y_abs}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return1y_abs}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return1y_abs,
    label: '1 Year Return',
    subLabel: '(% change over the past year)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // 3 year price
  {
    defaultValue: `["${filters.value_return3y_abs}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return3y_abs}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return3y_abs,
    label: '3 Year Return',
    subLabel: '(% change over the past 3 years)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // 5 year price
  {
    defaultValue: `["${filters.value_return5y_abs}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.value_return5y_abs}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.value_return5y_abs,
    label: '5 Year Return',
    subLabel: '(% change over the past 5 years)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // Market Cap
  {
    id: filters.marketcap, // what the API expects ie ['market_cap', 'between', 0, 300]
    label: 'Market Cap',
    labels: {
      0: '0M',
      10: '10M',
      19: '100M',
      28: '1000M',
      37: '10000M',
      40: '∞',
    },
    softMax: 37,
    min: 0,
    max: 40,
    // Must be JSON formatted. So no single quotes.
    rule: (range) =>
      `["${filters.marketcap}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map((value) => {
        if (typeof value === 'number') {
          if (value <= 10) {
            return value / 1;
          } else if (value <= 100) {
            return value / 10 + 9;
          } else if (value <= 1000) {
            return value / 100 + 18;
          } else if (value <= 10000) {
            return value / 1000 + 27;
          } else {
            return 40;
          }
        } else return value;
      }),
    // Must be JSON formatted. So no single quotes.
    defaultValue: `["${filters.marketcap}", "between", 0, ${API_INFINITY_VALUE}]`,
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === 0 && max === API_INFINITY_VALUE) {
        return 'is between $USD 0 and ∞';
      } else if (min !== 0 && max === API_INFINITY_VALUE) {
        return `is between $USD ${min} million and ∞`;
      } else if (min !== max) {
        return `is between $USD ${min} million and $USD ${max} million`;
      } else {
        return `is $USD ${min} million`;
      }
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) =>
      range.map((val) => {
        if (val <= 10) {
          return val * 1;
        } else if (val <= 19) {
          return (val - 9) * 10;
        } else if (val <= 28) {
          return (val - 18) * 100;
        } else if (val <= 37) {
          return (val - 27) * 1000;
        } else {
          return API_INFINITY_VALUE;
        }
      }),
  },
  // Share price
  {
    id: filters.sharePrice,
    label: 'Share Price',
    type: FILTER_TYPE.RANGE,
    labels: {
      ...dots(21, { step: 7 }),
      ...dots(38, { start: 21, step: 19 }),
      ...dots(62, { start: 59, step: 31 }),
      0: '0',
      21: '7',
      59: '45',
      121: '200',
      131: '∞',
    },
    defaultValue: `["${filters.sharePrice}", "between", 0, 200]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.sharePrice}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    min: 0,
    max: 131,
    softMax: 90,
    ruleToValue: ([_, __, ...range]: (number | string)[]) =>
      range.map((value) => {
        if (typeof value === 'number') {
          if (value <= 7) {
            return value * 3;
          } else if (value <= 45) {
            return 21 + Math.round((value - 7) / 2) * 2;
          } else if (value <= 200) {
            return 59 + Math.round((value - 45) / 5) * 2;
          } else {
            return 131;
          }
        } else return value;
      }),
    valueResolver: (range) =>
      range.map((value) => {
        if (value <= 21) {
          return Math.round(value / 3);
        } else if (value <= 59) {
          return Math.round((value - 21) / 2) * 2 + 7;
        } else if (value <= 121) {
          return Math.round((value - 59) / 2) * 5 + 45;
        } else {
          return API_INFINITY_VALUE;
        }
      }),
    titleFormatter: ([_, __, ...range]: (number | string)[]) => {
      const [left, right] = range;
      if (Number(left) >= 0 && right === API_INFINITY_VALUE) {
        return `is between ${left} and ∞`;
      } else if (right !== API_INFINITY_VALUE) {
        return `is between ${left} and ${right}`;
      } else {
        return `is ${left}`;
      }
    },
  },
  // Beta
  {
    id: 'beta',
    label: '5 Year Beta',
    subLabel: '(Share Price Volatility compared to Market)',
    labels: {
      ...dots(200, { step: 25 }),
      0: '0',
      100: '1',
      200: '2',
      210: '∞',
    },
    min: 0,
    max: 210,
    softMax: 200,
    defaultValue: `["beta", "between", 0, 2]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["beta", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, ...range]) =>
      range.map((value) => Number(value) * 100),
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) =>
      range.map((value) => (value <= 200 ? value / 100 : API_INFINITY_VALUE)),
    titleFormatter: ([_, __, ...range]) => {
      const [left, right] = range;
      if (Number(left) >= 0 && right === API_INFINITY_VALUE) {
        return `is between ${left} and ∞`;
      } else if (right !== API_INFINITY_VALUE) {
        return `is between ${left} and ${right}`;
      } else {
        return `is ${left}`;
      }
    },
  },
  // Days Since listing
  {
    id: filters.days_since_listing,
    label: 'Days Since Listing',
    subLabel: '(Number of days since listing or IPO)',
    labels: {
      ...dots(360, { start: 0, step: 10 }),
      0: '0',
      30: '30',
      90: '90',
      180: '180',
      360: '360',
    },
    defaultValue: `["${filters.days_since_listing}", "between", 0, 30]`,
    rule: (range) =>
      `["${filters.days_since_listing}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    min: 0,
    max: 360,
    type: FILTER_TYPE.RANGE,
    softMin: 0,
    softMax: 360,
    valueResolver: (range) => range.map((value) => value),
    titleFormatter,
  },
  // Days Since Earnings
  {
    id: filters.days_since_last_update,
    label: 'Days Since Earnings Update',
    subLabel: '(Number of days since last earnings)',
    labels: {
      ...dots(360, { start: 0, step: 10 }),
      0: '0',
      30: '30',
      90: '90',
      180: '180',
      360: '360',
    },
    defaultValue: `["${filters.days_since_last_update}", "between", 0, 30]`,
    rule: (range) =>
      `["${filters.days_since_last_update}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        return value;
      }),
    min: 0,
    max: 360,
    type: FILTER_TYPE.RANGE,
    softMin: 0,
    softMax: 360,
    valueResolver: (range) => range.map((value) => value),
    titleFormatter,
  },
  // Shares outstanding growth in 1 year
  {
    defaultValue: `["${filters.shares_outstanding_single_growth_1y}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.shares_outstanding_single_growth_1y}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.shares_outstanding_single_growth_1y,
    label: 'Shares Outstanding',
    subLabel: '(% change in number over the past year)',
    labels: {
      ...dots(205, { start: -100, step: 5 }),
      '-100': '-100%',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: -100,
    max: 105,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
];
